import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useUnifiedUser } from '@/hooks/useUnifiedUser';
import { supabase } from '@/integrations/supabase/client';
import { policyAcknowledgmentService } from '@/services/policyAcknowledgmentService';
import { Shield, Users, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { PolicyDocumentViewer } from '@/components/legal/PolicyDocumentViewer';

interface PendingChildConsent {
  childId: string;
  childUsername: string;
  childAcknowledgedAt: string;
}

const ParentConsent: React.FC = () => {
  const { user } = useUnifiedUser();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [connectionCode, setConnectionCode] = useState('');
  const [verifiedChild, setVerifiedChild] = useState<PendingChildConsent | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Policy checkboxes
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [cookiesAccepted, setCookiesAccepted] = useState(false);
  const [dataRightsAcknowledged, setDataRightsAcknowledged] = useState(false);
  
  // Signature
  const [signatureName, setSignatureName] = useState('');
  
  // Policy viewer
  const [viewingPolicy, setViewingPolicy] = useState<'privacy' | 'tos' | null>(null);

  const handleVerifyCode = async () => {
    if (!connectionCode || connectionCode.length !== 6) {
      toast({
        title: "Invalid Code",
        description: "Please enter a valid 6-character connection code.",
        variant: "destructive"
      });
      return;
    }

    setIsVerifying(true);
    try {
      // Find the child with this connection code
      const { data: codeData, error: codeError } = await supabase
        .from('child_connection_codes')
        .select('child_user_id')
        .eq('connection_code', connectionCode.toUpperCase())
        .eq('is_active', true)
        .maybeSingle();

      if (codeError || !codeData) {
        toast({
          title: "Code Not Found",
          description: "This connection code is invalid or expired.",
          variant: "destructive"
        });
        return;
      }

      // Get child's info and pending acknowledgment
      const { data: childData, error: childError } = await supabase
        .from('internal_users')
        .select('id, username')
        .eq('id', codeData.child_user_id)
        .single();

      if (childError || !childData) {
        toast({
          title: "Child Not Found",
          description: "Could not find the child associated with this code.",
          variant: "destructive"
        });
        return;
      }

      // Check if child has a pending acknowledgment
      const { data: ackData, error: ackError } = await supabase
        .from('user_policy_acknowledgments')
        .select('*')
        .eq('user_id', codeData.child_user_id)
        .eq('user_type', 'internal')
        .eq('child_acknowledged', true)
        .eq('parent_acknowledged', false)
        .maybeSingle();

      if (ackError || !ackData) {
        toast({
          title: "No Pending Consent",
          description: "This child does not have a pending consent request, or it has already been completed.",
          variant: "destructive"
        });
        return;
      }

      setVerifiedChild({
        childId: childData.id,
        childUsername: childData.username,
        childAcknowledgedAt: ackData.child_acknowledged_at
      });

      toast({
        title: "Child Verified!",
        description: `You can now provide consent for ${childData.username}.`,
      });

    } catch (error) {
      console.error('Error verifying code:', error);
      toast({
        title: "Error",
        description: "Could not verify the connection code. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmitConsent = async () => {
    if (!verifiedChild || !user) return;

    if (!privacyAccepted || !tosAccepted || !cookiesAccepted || !dataRightsAcknowledged) {
      toast({
        title: "All Policies Required",
        description: "Please review and accept all policies to provide consent.",
        variant: "destructive"
      });
      return;
    }

    if (!signatureName.trim()) {
      toast({
        title: "Signature Required",
        description: "Please enter your name as your electronic signature.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await policyAcknowledgmentService.completeParentConsent({
        childUserId: verifiedChild.childId,
        parentUserId: user.id,
        parentUserType: user.userType,
        parentSignatureName: signatureName.trim(),
        connectionCode: connectionCode.toUpperCase()
      });

      if (result.success) {
        toast({
          title: "Consent Provided!",
          description: `${verifiedChild.childUsername} can now access Players Lockeroom.`,
        });
        navigate('/dashboard');
      } else {
        toast({
          title: "Error",
          description: result.error || "Could not complete consent. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error submitting consent:', error);
      toast({
        title: "Error",
        description: "Could not complete consent. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const allChecked = privacyAccepted && tosAccepted && cookiesAccepted && dataRightsAcknowledged;

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>

        <Card className="bg-card border-border">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 bg-primary/20 rounded-full w-fit">
              <Users className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-2xl text-foreground">
              Your Child Needs Your Consent
            </CardTitle>
            <CardDescription>
              As required by COPPA, please review and consent to our policies on behalf of your child.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {!verifiedChild ? (
              // Step 1: Enter Connection Code
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="font-medium text-foreground mb-2">Step 1: Enter Connection Code</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Enter the 6-character code shown on your child's screen to verify their identity.
                  </p>
                  
                  <div className="flex gap-2">
                    <Input
                      value={connectionCode}
                      onChange={(e) => setConnectionCode(e.target.value.toUpperCase())}
                      placeholder="XXXXXX"
                      maxLength={6}
                      className="font-mono text-center text-lg tracking-widest uppercase"
                    />
                    <Button
                      onClick={handleVerifyCode}
                      disabled={isVerifying || connectionCode.length !== 6}
                    >
                      {isVerifying ? "Verifying..." : "Verify"}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              // Step 2: Review Policies and Provide Consent
              <div className="space-y-6">
                {/* Verified Child Info */}
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <div>
                      <p className="font-medium text-foreground">Child Verified</p>
                      <p className="text-sm text-muted-foreground">
                        Providing consent for: <strong>{verifiedChild.childUsername}</strong>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Policy Checkboxes */}
                <div className="space-y-4">
                  <h3 className="font-medium text-foreground">Step 2: Review and Accept Policies</h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                      <Checkbox
                        id="privacy"
                        checked={privacyAccepted}
                        onCheckedChange={(checked) => setPrivacyAccepted(!!checked)}
                      />
                      <div className="flex-1">
                        <Label htmlFor="privacy" className="text-sm cursor-pointer">
                          I have read and agree to the{' '}
                          <button
                            type="button"
                            onClick={() => setViewingPolicy('privacy')}
                            className="text-primary underline hover:no-underline"
                          >
                            Privacy Policy
                          </button>{' '}
                          on behalf of my child
                        </Label>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                      <Checkbox
                        id="tos"
                        checked={tosAccepted}
                        onCheckedChange={(checked) => setTosAccepted(!!checked)}
                      />
                      <div className="flex-1">
                        <Label htmlFor="tos" className="text-sm cursor-pointer">
                          I have read and agree to the{' '}
                          <button
                            type="button"
                            onClick={() => setViewingPolicy('tos')}
                            className="text-primary underline hover:no-underline"
                          >
                            Terms of Service
                          </button>{' '}
                          on behalf of my child
                        </Label>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                      <Checkbox
                        id="cookies"
                        checked={cookiesAccepted}
                        onCheckedChange={(checked) => setCookiesAccepted(!!checked)}
                      />
                      <Label htmlFor="cookies" className="text-sm cursor-pointer flex-1">
                        I consent to the use of necessary cookies for my child's account
                      </Label>
                    </div>

                    <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                      <Checkbox
                        id="dataRights"
                        checked={dataRightsAcknowledged}
                        onCheckedChange={(checked) => setDataRightsAcknowledged(!!checked)}
                      />
                      <Label htmlFor="dataRights" className="text-sm cursor-pointer flex-1">
                        I understand my child's data rights and how to request data deletion
                      </Label>
                    </div>
                  </div>
                </div>

                {/* Signature */}
                <div className="space-y-2">
                  <h3 className="font-medium text-foreground">Step 3: Electronic Signature</h3>
                  <p className="text-sm text-muted-foreground">
                    Type your full legal name below as your electronic signature confirming your consent.
                  </p>
                  <Input
                    value={signatureName}
                    onChange={(e) => setSignatureName(e.target.value)}
                    placeholder="Your Full Name"
                    className="font-medium"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  onClick={handleSubmitConsent}
                  disabled={isSubmitting || !allChecked || !signatureName.trim()}
                  className="w-full bg-primary hover:bg-primary/90"
                  size="lg"
                >
                  {isSubmitting ? (
                    "Processing..."
                  ) : (
                    <>
                      <Shield className="mr-2 h-4 w-4" />
                      I Consent on Behalf of My Child
                    </>
                  )}
                </Button>

                {/* Change Child Button */}
                <Button
                  variant="ghost"
                  onClick={() => {
                    setVerifiedChild(null);
                    setConnectionCode('');
                    setPrivacyAccepted(false);
                    setTosAccepted(false);
                    setCookiesAccepted(false);
                    setDataRightsAcknowledged(false);
                    setSignatureName('');
                  }}
                  className="w-full"
                >
                  Enter Different Connection Code
                </Button>
              </div>
            )}

            {/* Legal Notice */}
            <div className="pt-4 border-t border-border">
              <div className="flex gap-2 text-xs text-muted-foreground">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <p>
                  By providing consent, you confirm you are the parent or legal guardian of this child 
                  and authorize them to use Players Lockeroom in accordance with our policies and COPPA regulations.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Policy Viewer Modal */}
      {viewingPolicy && (
        <PolicyDocumentViewer
          policyType={viewingPolicy}
          open={!!viewingPolicy}
          onClose={() => setViewingPolicy(null)}
        />
      )}
    </div>
  );
};

export default ParentConsent;
